// ─── AI model catalogue ───────────────────────────────────────────────────────
//
// One description of each detection model, shared by the AI Modules page (per
// camera) and by Guided Setup's models step (which cameras + the site defaults).
//
// It lives here because the two screens set the SAME keys. While each kept its
// own copy, a default corrected on one screen stayed wrong on the other, and the
// wrong one wins whichever screen the customer happens to open second.
//
// Keys are the keys of DETECTOR_CLASSES in industrial_detectors.py. Enabling a
// model means adding its key to a camera's `detectors` list; its tunables are
// saved to `detector_config.<key>` on the camera, or to GLOBAL_DETECTOR_CONFIG
// for the whole site (resolved camera-over-site — see main.resolve_detector_config).

export const MODELS = [
  { key: 'helmet',        label: 'PPE / helmet',            icon: '⛑️', tier: 'CPU', desc: 'Helmet / vest compliance' },
  // Weapon / arms is hidden for now — not a focus, and armshf.pt is not shipped or
  // fetchable by download_models.py, so the row could only ever show MISSING. The
  // detector itself still exists in industrial_detectors; restore this line to expose it.
  { key: 'crowd',         label: 'Crowd',                   icon: '👥', tier: 'CPU', desc: 'Crowd density / grouping' },
  { key: 'fire',          label: 'Fire & smoke',            icon: '🔥', tier: 'CPU', desc: 'Smoke / fire persistence · YOLO11s · early warning, not a certified alarm' },
  { key: 'camera_blocked',label: 'Camera blocked / tamper', icon: '🚫', tier: 'CPU', desc: 'Lens covered / obstructed while streaming' },
  { key: 'fall',          label: 'Fall / person down',      icon: '🚑', tier: 'CPU', desc: 'Person collapsed and staying down — needs a side-on view, not overhead' },
  { key: 'face_cover',    label: 'Face covered (nose/mouth)', icon: '😷', tier: 'CPU', desc: 'Front-facing person, eyes or glasses visible, nose & mouth covered — needs an eye-level view. Starts in Learning mode (logs only)' },
];

// Face-identity detectors — toggled together by the per-camera "Face recognition"
// switch. ON runs unknown (access control) + known (logging for attendance/known-seen).
export const FACE_AWARE = ['unknown', 'known', 'banned', 'after_hours'];

// Identity, as two questions a site answers per camera. Kept OUT of MODELS so the
// AI Models page's grid is unchanged — it drives those from its own face switch —
// while Guided Setup can ask them as plainly as it asks about PPE.
//
// `instant` is the other half of the face switch: with face recognition off, a
// camera still alerts on a PERSON, it just does not work out who. Something has
// to run, or the camera stops alerting altogether.
export const FACE_OFF_DETECTOR = 'instant';

// ...unless something else is already alerting, which is the flat-door case.
//
// A corridor or a flat door wants LOITERING ONLY: residents walk past all day, so
// a person-level detector buries the one alert that matters. zone_watch IS the
// something that runs, so substituting `instant` there replaces "alerts on
// strangers" with "alerts on everyone" — strictly worse than the state it
// replaced, and the UI offered no way back.
//
// Measured at B105 pathway, 03-11-2026: turning the face switch off produced two
// "Person detected" alerts in three minutes, and reaching the wanted set needed a
// config.json edit that the running process then overwrote.
// NOT after_hours: it is FACE_AWARE, so switching face recognition off removes it
// too — something being taken away cannot be the thing that keeps alerting. With
// it listed here, a camera running ['unknown','after_hours'] came out of the
// switch with an EMPTY detector list and alerted on nothing at all.
export const SELF_ALERTING_DETECTORS = ['zone_watch'];
// Pass what the camera will be left with AFTER the face detectors are removed,
// not what it has now — the question is whether anything is still alerting once
// the switch has done its work.
export const needsFallbackAlerter = dets =>
  !(dets || []).some(k => SELF_ALERTING_DETECTORS.includes(k));
export const IDENTITY = {
  face: {
    key: 'face', icon: '🧑', title: 'Recognise faces',
    on:  'Alerts on people it does not recognise, stays quiet for enrolled staff, and logs who it saw for attendance.',
    off: 'Alerts on ANY person, without working out who — lighter on the box, and no faces are matched.',
  },
  after_hours: {
    key: 'after_hours', icon: '🌙', title: 'After the site closes',
    desc: 'Once the site has shut, ANY person on this camera raises an alert — recognised or not.',
  },
};
// What "Face recognition ON" enables by default: unauthorised-access + known logging +
// banned watchlist (security default). The Banned checkbox can turn the watchlist off.
export const FACE_ON_SET = ['unknown', 'known', 'banned'];

// ─── Per-model tunables ───────────────────────────────────────────────────────
//
// Saved to detector_config.<key> — per camera, or site-wide in
// GLOBAL_DETECTOR_CONFIG. Two rules here, and both matter:
//
// 1. `def` MUST equal the detector's own default, exactly. These are not
//    suggestions: a screen that shows a non-default as the default tells the
//    customer the system behaves one way while it behaves another — and the
//    moment anything is saved, the lie becomes true. Fire shipped with two of
//    these (persist 3 vs the code's 2, require_growth true vs the code's False),
//    so saving any fire change silently tightened the smoke gate. If you change
//    a default here, change it in industrial_detectors.py, or don't change it.
//
// 2. `ask: true` marks a setting whose answer is a FACT ABOUT THE SITE that no
//    default can get right — is this camera outdoors, how many people is a crowd
//    here. Everything else has an answer that is right almost everywhere, and is
//    hidden behind Advanced rather than put to a customer who has no basis to
//    answer it. Hidden, not absent: it is one tap away and says when it has been
//    changed.
//
// Saves send only values that DIFFER from `def` (see nonDefault), so an untouched
// setting is never written and the detector's own default keeps governing.
export const MODEL_SETTINGS = {
  // PPE (SH17). Image size — NOT confidence — is the lever that decides whether a helmet
  // is seen at all: measured on site frames, a real helmet is missed at imgsz 1280 at
  // every confidence down to 0.05, and found at 0.42 at 1920. Leave imgsz at 1920 unless
  // the box is CPU-starved. Each pass costs ~1.5s, hence the interval.
  helmet: [
    { key: 'conf',          label: 'Confidence',           type: 'range', min: 0.05, max: 0.9,  step: 0.05, def: 0.25 },
    { key: 'imgsz',         label: 'Image size',           type: 'int',   min: 640,  max: 1920, step: 320,  def: 1920 },
    { key: 'interval_secs', label: 'Seconds between scans',type: 'int',   min: 1,    max: 60,   step: 1,    def: 3 },
    { key: 'persist',       label: 'Persistence (scans)',  type: 'int',   min: 1,    max: 10,   step: 1,    def: 2 },
  ],
  fire: [
    // The one genuine site question: outdoors, sky and haze trip the smoke path.
    { key: 'smoke',           label: 'Watch for smoke as well as flame', type: 'bool', def: true, ask: true,
      hint: 'Turn this OFF outdoors — sky, haze and bright cloud all read as smoke. Flame detection is unaffected.' },
    { key: 'conf',            label: 'Confidence',            type: 'range', min: 0.2,   max: 0.9,  step: 0.05,  def: 0.45 },
    { key: 'persist',         label: 'Persistence (frames)',  type: 'int',   min: 1,     max: 10,   step: 1,     def: 2 },
    { key: 'nof_window',      label: 'Counted within (frames)', type: 'int', min: 1,     max: 30,   step: 1,     def: 5,
      hint: 'The window those persistence hits have to land in.' },
    { key: 'imgsz',           label: 'Image size',            type: 'int',   min: 640,   max: 1920, step: 320,   def: 640,
      hint: 'Raise it to see thin smoke further away, at CPU cost on every pass.' },
    { key: 'require_flicker', label: 'Require flame flicker', type: 'bool',  def: true,  hint: 'temporal: rejects a STATIONARY orange object (parked vest). Someone walking in orange clothing still passes — the gate sees movement, not flame' },
    { key: 'require_motion',  label: 'Require smoke to drift', type: 'bool', def: true,
      hint: 'The smoke equivalent of flicker, and the gate that replaced plume growth — it is what rejects a grey wall. Leave ON.' },
    { key: 'smoke_motion_min',label: 'Least drift to accept',  type: 'range', min: 0.5,  max: 10,   step: 0.5,   def: 2.0,
      hint: 'How much in-place movement the grey has to show.' },
    { key: 'require_growth',  label: 'Require plume growth',  type: 'bool',  def: false,
      hint: 'A stricter smoke gate ON TOP of drift, off by default — drift already rejects static grey. Turning it on makes real smoke harder to catch, not easier.' },
    { key: 'reject_on_person',label: 'Reject static on people',type: 'bool', def: true,  hint: 'soft filter; a flickering/growing fire on a person still alerts' },
    { key: 'grey_frac_min',   label: 'Smoke-grey floor',      type: 'range', min: 0.05,  max: 0.8,  step: 0.05,  def: 0.30,
      hint: 'How much of the box must be smoke-grey before it is a smoke candidate at all.' },
    // Floor is 0.001, not the old 0.002: the backend accepts lower and a live site
    // needed 0.001, but the slider could not express its own default's neighbourhood
    // — the only way to set it was the API, which is not a setting the field can reach.
    { key: 'min_fire_ratio',  label: 'Min fire size',         type: 'range', min: 0.001, max: 0.05, step: 0.001, def: 0.002 },
    { key: 'min_smoke_ratio', label: 'Min smoke size',        type: 'range', min: 0.004, max: 0.06, step: 0.002, def: 0.004 },
    { key: 'fire_color_frac', label: 'Flame colour strictness', type: 'range', min: 0.02, max: 0.40, step: 0.01, def: 0.15,
      hint: 'how much of the box must be saturated orange. LOWER = more flames pass. A real flame’s bright core is near-white, and the box includes ground/smoke, so 0.15 rejects genuine daylight fire — check orange_frac in the log and set just under it' },
    { key: 'fire_sat_min',    label: 'Flame colour saturation floor', type: 'int', min: 40, max: 220, step: 10, def: 150,
      hint: 'prefer lowering the strictness above instead. This floor is what rejects night IR glare (white) and tan walls — dropping it lets both back in' },
    { key: 'fire_val_min',    label: 'Flame brightness floor', type: 'int',  min: 40, max: 220, step: 10, def: 150,
      hint: 'Dark orange is not flame. Same warning as the floor above — lowering it lets tan walls back in.' },
  ],
  crowd: [
    // How many people make a crowd is a fact about the place, not a default.
    { key: 'min_group_size', label: 'People together before it counts as a crowd',
      type: 'int', min: 2, max: 20, step: 1, def: 5, ask: true },
    { key: 'proximity_distance', label: 'Standing within (px) counts as one group',
      type: 'int', min: 30, max: 400, step: 10, def: 120,
      hint: 'In camera pixels, so a wide-angle view needs a smaller number than a close one.' },
    { key: 'density_threshold', label: 'Crowding (people per pixel area)',
      type: 'range', min: 0.0005, max: 0.01, step: 0.0005, def: 0.002 },
  ],
  camera_blocked: [
    { key: 'persist_secs', label: 'Hold before alert (s)',       type: 'int',   min: 3, max: 60, step: 1, def: 10 },
    { key: 'lap_floor',    label: 'Detail floor (higher = more sensitive)', type: 'range', min: 5, max: 60, step: 1, def: 12 },
    { key: 'std_floor',    label: 'Flatness floor',  type: 'range', min: 2, max: 30, step: 1, def: 8,
      hint: 'Catches a solid, even cover — a cloth or a bag over the lens, where there is detail nowhere.' },
    { key: 'drop_ratio',   label: 'Detail drop against normal', type: 'range', min: 0.05, max: 0.9, step: 0.05, def: 0.35,
      hint: 'Judged against what this camera normally looks like, so it works on a view that is blurry to begin with.' },
  ],
  // Fall. The box shape is only a pre-filter — the TORSO ANGLE is what separates a
  // fall from a worker sitting or squatting on the floor (both make a wide, short
  // box; only one has a horizontal torso), so move it last and only on log evidence.
  // "Must be seen standing first" is the false-positive guard: with it off, anything
  // horizontal — cloth on a line, stacked sacks — can alert.
  fall: [
    { key: 'dwell_secs',         label: 'Hold before alert (s)',     type: 'int',  min: 3,  max: 60,  step: 1,  def: 8 },
    { key: 'torso_angle',        label: 'Torso angle for "down"',    type: 'int',  min: 45, max: 85,  step: 5,  def: 60, hint: 'degrees off vertical — lower = more sensitive' },
    { key: 'sample_secs',        label: 'Seconds between scans',     type: 'int',  min: 1,  max: 15,  step: 1,  def: 3 },
    { key: 'require_transition', label: 'Must be seen standing first', type: 'bool', def: true, hint: 'rejects laundry / sacks / anyone already down — leave ON' },
    { key: 'escalate_secs',      label: 'Re-alert while down (s)',   type: 'int',  min: 60, max: 600, step: 30, def: 120 },
    { key: 'always_on',          label: 'Ignore alert schedule',     type: 'bool', def: true, hint: 'a fall is a medical event — must fire in work hours too' },
    { key: 'conf',               label: 'Pose confidence',           type: 'range', min: 0.1, max: 0.9, step: 0.05, def: 0.35 },
    { key: 'imgsz',              label: 'Image size',                type: 'int',  min: 640, max: 1920, step: 320, def: 640 },
    { key: 'sample_active_secs', label: 'Seconds between scans while somebody is down (s)',
      type: 'int', min: 1, max: 10, step: 1, def: 1,
      hint: 'Faster once there is something to watch, so the dwell timer is accurate.' },
    { key: 'upright_angle',      label: 'Torso angle still counting as standing', type: 'int',
      min: 10, max: 60, step: 5, def: 40,
      hint: 'The other half of “seen standing first”. Between this and the down angle the system holds its judgement.' },
    { key: 'upright_ratio',      label: 'Standing box shape',        type: 'range', min: 0.2, max: 1.5, step: 0.05, def: 0.60,
      hint: 'Width over height. A standing person is taller than wide.' },
    { key: 'aspect_ratio',       label: 'Down box shape',            type: 'range', min: 0.5, max: 3, step: 0.05, def: 0.90,
      hint: 'A pre-filter only — the torso angle is what separates a fall from somebody squatting, since both make a wide box.' },
    { key: 'min_upright_samples',label: 'Scans standing before a fall counts', type: 'int',
      min: 1, max: 10, step: 1, def: 2 },
    { key: 'recover_samples',    label: 'Scans upright to call it over', type: 'int', min: 1, max: 10, step: 1, def: 2 },
    { key: 'move_tolerance',     label: 'Movement still counted as staying down', type: 'range',
      min: 0.1, max: 5, step: 0.1, def: 1.0,
      hint: 'Someone who has fallen still shifts. Too strict and rolling over cancels the alert.' },
    { key: 'lost_secs',          label: 'Forget a person after (s)', type: 'int', min: 5, max: 180, step: 5, def: 30 },
    { key: 'min_box_h',          label: 'Ignore people smaller than (px tall)', type: 'int',
      min: 20, max: 300, step: 10, def: 60,
      hint: 'Too far away to judge a posture from.' },
    { key: 'max_escalations',    label: 'Most re-alerts for one fall', type: 'int', min: 0, max: 10, step: 1, def: 3 },
  ],
  // Face cover. `def` = face_cover_geom.DEFAULTS / PRESETS["medium"] exactly
  // (test_face_cover_feedback.py checks it). The evidence bars (samples, skin and
  // CLIP margins) are NOT sliders: they follow Sensitivity, and a slider showing the
  // medium value while Low is selected would misstate what runs. Per-camera
  // overrides of those come from tuning suggestions and are listed, with a Reset,
  // on the module's health card.
  face_cover: [
    { key: 'observe_only', label: 'Learning mode (log only, no alerts)', type: 'bool', def: true,
      hint: 'Leave on for the first week, label what it logged, then turn off.' },
    { key: 'sensitivity',  label: 'Sensitivity', type: 'select', def: 'medium',
      options: [['low', 'Low — fewer alerts'], ['medium', 'Medium'], ['high', 'High — more alerts']],
      hint: 'How much evidence before an alert. Never lets the back of a head through.' },
    { key: 'quick_alert',  label: 'Quick alerts for walk-throughs', type: 'bool', def: false,
      hint: 'One clearly covered frame raises a “possible face cover” alert, for people who do not stop. Checks twice as often, and more false alarms. Daylight colour only.' },
    { key: 'after_clear_alert', label: 'Alert when covered after entering', type: 'bool', def: false,
      hint: 'A face seen bare, then covered for about 5 checks in a row, raises a “possible face cover” alert. A beard or shadow can also trigger it.' },
    { key: 'siren',       label: 'Sound the siren', type: 'bool', def: false,
      hint: 'Only for cameras without Unknown person. With it on, this alert replaces it and the camera’s own siren, push and schedule settings decide.' },
    { key: 'always_on',    label: 'Ignore alert schedule', type: 'bool', def: false,
      hint: 'Use its own active hours instead of the camera’s alert window. Only for cameras without Unknown person.' },
    { key: 'min_head_px',  label: 'Ignore faces smaller than (px)', type: 'int', min: 32, max: 200, step: 8, def: 64,
      hint: 'Too far away to judge a mask.' },
    { key: 'yaw_max',      label: 'How frontal a face must be', type: 'range', min: 0.1, max: 0.5, step: 0.05, def: 0.30,
      hint: 'Lower = only faces looking straight at the camera.' },
    { key: 'sample_secs',  label: 'Seconds between checks', type: 'range', min: 0.5, max: 5, step: 0.5, def: 1.0 },
    { key: 'max_persons',  label: 'People checked at once', type: 'int', min: 1, max: 10, step: 1, def: 4,
      hint: 'Largest faces first. The CPU throttle on busy cameras.' },
    { key: 'lost_secs',    label: 'Forget a person after (s)', type: 'int', min: 5, max: 60, step: 5, def: 10 },
  ],
  // After-hours: anybody at all, once the site has closed. Both of these are
  // facts about the site, so both are asked.
  after_hours: [
    { key: 'closing_time', label: 'The site closes at', type: 'time', def: '18:00', ask: true,
      hint: 'After this, ANY person on this camera raises an alert — recognised or not. There is no second window: it runs until midnight.' },
  ],
};

// PPE items the SH17 model can enforce. It detects the gear AND the matching body part,
// so a violation is "body part with no gear on it" (head with no helmet). Helmet is the
// pair validated on site footage; the rest use the same mechanism but are unproven here,
// hence the note in the UI.
export const PPE_ITEMS = [
  { key: 'helmet',  label: 'Helmet',  icon: '⛑️', proven: true },
  { key: 'vest',    label: 'Vest',    icon: '🦺' },
  { key: 'gloves',  label: 'Gloves',  icon: '🧤' },
  { key: 'goggles', label: 'Goggles', icon: '🥽' },
  { key: 'mask',    label: 'Mask',    icon: '😷' },
  { key: 'shoes',   label: 'Shoes',   icon: '🥾' },
];

// ─── Load estimate ────────────────────────────────────────────────────────────
//
// These boxes are CPU-only and run every camera on the same few cores, so the
// cost of a model is per camera and it adds up fast. Guided Setup shows an
// estimate rather than letting someone tick four models across eight cameras and
// meet the result later as an fps sag with no obvious cause.
//
// It is an ESTIMATE and the UI says so. Only one of these costs is documented in
// the code — a PPE pass at imgsz 1920 takes ~1.5s (see MODEL_SETTINGS.helmet) —
// and the rest are deliberately conservative guesses of the same shape. They are
// here, in one place, so a real measurement replaces a guess everywhere at once.
//
//   secsPerPass   CPU seconds one inference pass costs
//   passesPerSec  how often it runs, given that model's own settings
const LOAD = {
  // Scales with pixels, so image size matters more than anything else here.
  helmet: {
    secsPerPass: s => 1.5 * Math.pow((s.imgsz || 1920) / 1920, 2),
    passesPerSec: s => 1 / Math.max(1, s.interval_secs || 3),
  },
  fall: {
    secsPerPass: () => 0.5,                                   // a pose pass
    passesPerSec: s => 1 / Math.max(1, s.sample_secs || 3),
  },
  // No interval of its own — it runs inside the detection cycle. Counted at one
  // pass a second, which is conservative on a box whose cycle is slower.
  fire:           { secsPerPass: () => 0.25, passesPerSec: () => 1 },
  // Reads the person detections the pipeline already made.
  crowd:          { secsPerPass: () => 0,    passesPerSec: () => 0 },
  // Laplacian variance on a downscaled frame.
  camera_blocked: { secsPerPass: () => 0.01, passesPerSec: () => 1 },
  // Per judged person: RTMPose + face detection on a head crop + (on clear faces or
  // non-colour frames) one CLIP pass. Only while people are in view.
  face_cover: {
    secsPerPass: () => 0.3,
    passesPerSec: s => 1 / Math.max(0.5, s.sample_secs || 1),
  },
};

// Estimated CPU-seconds per second of video for one model on one camera.
export function modelLoad(key, settings = {}) {
  const l = LOAD[key];
  if (!l) return 0;
  return l.secsPerPass(settings) * l.passesPerSec(settings);
}

// Settings for a model with every default filled in, so a load estimate never
// reads undefined and a slider always has a value to show.
export function withDefaults(key, saved = {}) {
  const out = {};
  (MODEL_SETTINGS[key] || []).forEach(s => {
    out[s.key] = saved[s.key] != null ? saved[s.key] : s.def;
  });
  return out;
}

// The settings a customer is actually asked (a fact about the site), and the rest.
export const askSettings   = key => (MODEL_SETTINGS[key] || []).filter(s => s.ask);
export const tunedSettings = key => (MODEL_SETTINGS[key] || []).filter(s => !s.ask);

// What to SAVE: only values that differ from the detector's own default.
//
// This is the whole reason the stored config stays small and honest. Writing
// every slider at its displayed value is what pinned fire's require_growth to a
// value the code does not default to — once written, a stored value wins forever,
// and nobody can tell a deliberate choice from a screen that happened to be
// opened. A key already stored and now set back to the default returns `null`,
// which the PUT merges pop, so "reset" really resets instead of pinning the
// default as an override.
export function nonDefault(key, values = {}, stored = {}) {
  const out = {};
  (MODEL_SETTINGS[key] || []).forEach(s => {
    const v    = values[s.key];
    const same = v === undefined || v === null || sameValue(v, s.def);
    if (!same)                        out[s.key] = v;
    else if (s.key in (stored || {})) out[s.key] = null;   // stored → clear it
  });
  return out;
}

// Numbers come back off a range input as floats, so 0.3 and 0.30 must compare
// equal without making 0.3 equal to 0.35.
function sameValue(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) < 1e-9;
  return a === b;
}
